import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCart } from "../../context/CartContext";
import { X, Trash2, Plus, Minus, ShoppingBag, ArrowRight, Truck } from "lucide-react";
import { api } from "../../services/api";
import { toast } from "sonner";

export default function CartDrawer() {
  const { cart, isOpen, setIsOpen, updateQuantity, removeItem } = useCart();
  const [cep, setCep] = useState("");
  const [shippingQuote, setShippingQuote] = useState(null);
  const [loadingShipping, setLoadingShipping] = useState(false);
  const navigate = useNavigate();

  if (!isOpen) return null;

  const handleCalculateShipping = async (e) => {
    e.preventDefault();
    const cleanCep = cep.replace(/\D/g, "");
    if (cleanCep.length !== 8) {
      toast.error("Informe um CEP válido com 8 dígitos.");
      return;
    }

    setLoadingShipping(true);
    try {
      const res = await api.post("/shipping/quote", {
        destination_cep: cleanCep,
        items: cart.items.map((i) => ({ product_id: i.product_id, quantity: i.quantity })),
      });
      setShippingQuote(res.data.options);
      toast.success("Cotação de frete Correios atualizada!");
    } catch (err) {
      toast.error("Não foi possível calcular o frete para este CEP.");
    } finally {
      setLoadingShipping(false);
    }
  };

  const handleProceedCheckout = () => {
    setIsOpen(false);
    navigate("/checkout");
  };

  const freeShippingProgress = Math.min(
    100,
    Math.round((cart.subtotal_in_cents / 29900) * 100)
  );

  return (
    <div className="fixed inset-0 z-50 overflow-hidden animate-in fade-in">
      {/* Backdrop */}
      <div
        onClick={() => setIsOpen(false)}
        className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity"
      />

      <div className="fixed inset-y-0 right-0 max-w-full flex pl-10">
        <div className="w-screen max-w-md bg-[#121214] border-l border-white/10 shadow-2xl flex flex-col text-white">
          
          {/* Header */}
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 rounded-xl bg-[#18181B] border border-white/10 flex items-center justify-center text-[#FF8C69]">
                <ShoppingBag className="w-5 h-5" />
              </div>
              <div>
                <h3 className="font-heading text-lg font-bold">Meu Carrinho</h3>
                <p className="text-xs text-[#A1A1AA]">{cart.item_count} {cart.item_count === 1 ? "item" : "itens"}</p>
              </div>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              data-testid="cart-drawer-close-btn"
              className="p-2 rounded-xl text-[#A1A1AA] hover:text-white hover:bg-white/5"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Free Shipping Progress Bar */}
          <div className="px-6 py-3 bg-[#18181B] border-b border-white/10 text-xs">
            <div className="flex items-center justify-between mb-1.5">
              <span className="flex items-center gap-1.5 text-white">
                <Truck className="w-3.5 h-3.5 text-[#FF8C69]" />
                {cart.free_shipping_remaining_in_cents === 0 ? (
                  <strong className="text-[#10B981]">Frete Grátis PAC liberado!</strong>
                ) : (
                  <span>
                    Faltam <strong>R$ {(cart.free_shipping_remaining_in_cents / 100).toFixed(2).replace(".", ",")}</strong> para Frete Grátis
                  </span>
                )}
              </span>
              <span className="text-[10px] text-[#A1A1AA]">{freeShippingProgress}%</span>
            </div>
            <div className="w-full bg-[#121214] h-1.5 rounded-full overflow-hidden">
              <div
                className="h-full bg-[#FF8C69] transition-all duration-300"
                style={{ width: `${freeShippingProgress}%` }}
              />
            </div>
          </div>

          {/* Items List */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {cart.items.length === 0 ? (
              <div className="text-center py-16 space-y-4">
                <div className="w-16 h-16 rounded-full bg-[#18181B] mx-auto flex items-center justify-center text-[#A1A1AA]">
                  <ShoppingBag className="w-8 h-8 opacity-40" />
                </div>
                <p className="font-heading text-lg text-white">Seu carrinho está vazio</p>
                <p className="text-xs text-[#A1A1AA] max-w-xs mx-auto">
                  Explore nosso catálogo e adicione seus itens favoritos.
                </p>
                <button
                  onClick={() => setIsOpen(false)}
                  data-testid="explore-catalog-btn"
                  className="mt-4 px-6 py-2.5 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-[#0A0A0B] text-xs font-bold"
                >
                  Explorar Catálogo
                </button>
              </div>
            ) : (
              cart.items.map((item) => (
                <div
                  key={item.product_id}
                  data-testid={`cart-item-${item.product_id}`}
                  className="flex gap-4 p-3 rounded-2xl bg-[#18181B] border border-white/10"
                >
                  <img
                    src={item.main_media_url}
                    alt={item.name}
                    className="w-20 h-24 object-cover rounded-xl bg-[#121214]"
                  />
                  <div className="flex-1 flex flex-col justify-between">
                    <div>
                      <h4 className="font-heading text-xs font-bold text-white line-clamp-2">
                        {item.name}
                      </h4>
                      <p className="text-[11px] text-[#FF8C69] font-bold mt-1">
                        {item.formatted_price}
                      </p>
                    </div>

                    <div className="flex items-center justify-between pt-2">
                      <div className="flex items-center gap-2 bg-[#121214] border border-white/10 rounded-xl p-1 text-xs">
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity - 1)}
                          data-testid={`cart-qty-decrease-${item.product_id}`}
                          className="p-1 hover:text-[#FF8C69]"
                        >
                          <Minus className="w-3 h-3" />
                        </button>
                        <span className="px-2 font-mono text-xs">{item.quantity}</span>
                        <button
                          onClick={() => updateQuantity(item.product_id, item.quantity + 1)}
                          data-testid={`cart-qty-increase-${item.product_id}`}
                          className="p-1 hover:text-[#FF8C69]"
                        >
                          <Plus className="w-3 h-3" />
                        </button>
                      </div>

                      <button
                        onClick={() => removeItem(item.product_id)}
                        data-testid={`cart-item-remove-${item.product_id}`}
                        className="text-[#A1A1AA] hover:text-red-400 p-1.5 transition-colors"
                        title="Remover item"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer & Checkout Action */}
          {cart.items.length > 0 && (
            <div className="p-6 bg-[#18181B] border-t border-white/10 space-y-4">
              
              {/* Freight Simulator */}
              <form onSubmit={handleCalculateShipping} className="space-y-2">
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={cep}
                    onChange={(e) => setCep(e.target.value)}
                    placeholder="Calcular CEP"
                    maxLength={9}
                    data-testid="cart-cep-input"
                    className="flex-1 bg-[#121214] border border-white/10 rounded-xl px-3 py-2 text-xs text-white placeholder-[#A1A1AA]/50 focus:outline-none focus:border-[#FF8C69]"
                  />
                  <button
                    type="submit"
                    disabled={loadingShipping}
                    data-testid="cart-cep-submit-btn"
                    className="px-4 py-2 bg-[#FF8C69] hover:bg-[#FFA285] rounded-xl text-xs font-bold text-[#0A0A0B]"
                  >
                    {loadingShipping ? "..." : "Calcular"}
                  </button>
                </div>
                {shippingQuote && (
                  <div className="p-2.5 rounded-xl bg-[#121214] border border-white/10 space-y-1 text-xs">
                    {shippingQuote.map((opt) => (
                      <div key={opt.service_code} className="flex items-center justify-between text-[#A1A1AA]">
                        <span>{opt.service_name} ({opt.estimated_days} dias):</span>
                        <strong className="text-white">{opt.formatted_price}</strong>
                      </div>
                    ))}
                  </div>
                )}
              </form>

              {/* Subtotal */}
              <div className="pt-2 border-t border-white/10 flex items-center justify-between text-sm">
                <span className="text-[#A1A1AA]">Subtotal:</span>
                <span className="font-heading text-xl font-bold text-[#FF8C69]">
                  {cart.formatted_subtotal}
                </span>
              </div>

              {/* Checkout CTA */}
              <button
                onClick={handleProceedCheckout}
                data-testid="proceed-to-checkout-btn"
                className="w-full py-4 rounded-xl bg-[#FF8C69] hover:bg-[#FFA285] text-[#0A0A0B] text-xs font-bold tracking-wider uppercase shadow-[0_4px_25px_rgba(255,140,105,0.3)] transition-all flex items-center justify-center gap-2"
              >
                <span>Finalizar Pedido</span>
                <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
